import { useEffect, useLayoutEffect } from 'react';
import { useIoContext } from '~/contexts';
import { useAppConfig } from '~/store/store-context';
import styles from './AgentChangeHighlight.module.scss';
import { AGREEMENT_SETTINGS_TARGETS, PAYMENT_SETTINGS_TARGETS } from './agentTargetSets';
// Covers the signal -> useSignal setTimeout(0) -> ServicePackages effect
// -> setSelectedPackage -> commit chain that lands a service in DOM after
// the initial rAF tick. Empirically ~30-50ms; padded for slower devices.
const LATE_MOUNT_DELAY_MS = 150;
export const useAgentChangeHighlightCoordinator = (param)=>{
    let { changedTargets, setShowAgreementSettings, setShowPaymentSettings, signalScrollToServiceId, signalSwitchToBundleId } = param;
    const { browser: { scrollIntoView } } = useIoContext();
    const { disableScrollAnimation } = useAppConfig();
    const key = changedTargets.join('|');
    useLayoutEffect(()=>{
        if (changedTargets.length === 0) return;
        if (changedTargets.some((t)=>AGREEMENT_SETTINGS_TARGETS.has(t))) {
            setShowAgreementSettings(true);
        }
        if (changedTargets.some((t)=>PAYMENT_SETTINGS_TARGETS.has(t))) {
            setShowPaymentSettings(true);
        }
        const bundleToken = changedTargets.find((t)=>t.startsWith('bundle:'));
        if (bundleToken != null) {
            signalSwitchToBundleId(bundleToken.slice('bundle:'.length));
        }
        const serviceToken = changedTargets.find((t)=>t.startsWith('service:'));
        if (serviceToken != null) {
            signalScrollToServiceId(serviceToken.slice('service:'.length));
        }
    }, [
        key
    ]);
    useEffect(()=>{
        if (changedTargets.length === 0) return;
        const set = new Set(changedTargets);
        const highlighted = new WeakSet();
        const highlightMatches = ()=>{
            document.querySelectorAll('[data-agent-target]').forEach((node)=>{
                const value = node.dataset.agentTarget;
                if (value == null || !set.has(value) || highlighted.has(node)) return;
                highlighted.add(node);
                node.classList.add(styles.highlight);
                const onAnimationEnd = (event)=>{
                    if (event.target !== node) return;
                    if (event.animationName !== styles.agentHighlightFade) return;
                    node.classList.remove(styles.highlight);
                    node.removeEventListener('animationend', onAnimationEnd);
                };
                node.addEventListener('animationend', onAnimationEnd);
            });
        };
        const scrollToFirstField = ()=>{
            const first = Array.from(document.querySelectorAll('[data-agent-target]')).find((node)=>{
                const value = node.dataset.agentTarget;
                return value != null && set.has(value) && !value.startsWith('service:') && !value.startsWith('bundle:');
            });
            if (first != null) scrollIntoView(first, {
                disableScrollAnimation
            });
        };
        let cancelled = false;
        const rafId = requestAnimationFrame(()=>{
            if (cancelled) return;
            highlightMatches();
            scrollToFirstField();
        });
        const lateMountTimeoutId = setTimeout(()=>{
            if (cancelled) return;
            highlightMatches();
        }, LATE_MOUNT_DELAY_MS);
        return ()=>{
            cancelled = true;
            cancelAnimationFrame(rafId);
            clearTimeout(lateMountTimeoutId);
        };
    }, [
        key
    ]);
};
